/**
 * Cicha Pewność w palecie marki klienta - panel cookies komunikuje wybór jasno i pozwala wrócić do ustawień bez presji.
 */
import { useEffect, useRef, useState } from "react";
import { Cookie, Settings2 } from "lucide-react";
import {
  activateGtmForDebug,
  applyCookiePreferences,
  initializeGoogleConsentMode,
} from "@/lib/analytics";
import {
  CONSENT_EVENT_NAME,
  cookieCategories,
  defaultCookiePreferences,
  downloadConsentRegister,
  getConsentRecord,
  saveCookieConsent,
  type CookiePreferences,
  type ConsentRecord,
} from "@/lib/cookie-consent";

export function CookieConsent() {
  const [visible, setVisible] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(
    defaultCookiePreferences
  );
  const panelRef = useRef<HTMLDivElement>(null);
  const primaryActionRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    initializeGoogleConsentMode();
    activateGtmForDebug();
    const stored = getConsentRecord();
    if (stored) {
      setPreferences(stored);
      applyCookiePreferences(stored);
      setVisible(false);
    }
    const openSettings = () => {
      returnFocusRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      const latest = getConsentRecord();
      setPreferences(latest ?? defaultCookiePreferences);
      setShowSettings(true);
      setVisible(true);
    };
    const applyStoredPreferences = (event: Event) => {
      const record = (event as CustomEvent<ConsentRecord>).detail;
      if (record) {
        setPreferences(record);
        applyCookiePreferences(record);
        setVisible(false);
      }
    };
    window.addEventListener("open-cookie-settings", openSettings);
    window.addEventListener(CONSENT_EVENT_NAME, applyStoredPreferences);
    return () => {
      window.removeEventListener("open-cookie-settings", openSettings);
      window.removeEventListener(CONSENT_EVENT_NAME, applyStoredPreferences);
    };
  }, []);

  useEffect(() => {
    if (!visible) return;
    window.requestAnimationFrame(() => primaryActionRef.current?.focus());
    const trapFocus = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setVisible(false);
        setShowSettings(false);
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;
      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(
          "a[href], button:not([disabled]), input:not([disabled])"
        )
      ).filter(item => !item.hasAttribute("hidden"));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", trapFocus);
    return () => window.removeEventListener("keydown", trapFocus);
  }, [visible, showSettings]);

  useEffect(() => {
    document.documentElement.classList.toggle("cookie-dialog-open", visible);
    return () =>
      document.documentElement.classList.remove("cookie-dialog-open");
  }, [visible]);

  useEffect(() => {
    if (visible || !returnFocusRef.current) return;
    const opener = returnFocusRef.current;
    returnFocusRef.current = null;
    window.requestAnimationFrame(() => {
      if (opener.isConnected) opener.focus();
    });
  }, [visible]);

  const save = (next: CookiePreferences) => {
    const previous = getConsentRecord();
    const shouldReloadAfterOptionalConsentRevocation =
      Boolean(previous?.analytics || previous?.marketing) &&
      !next.analytics &&
      !next.marketing;
    const record = saveCookieConsent(next);
    if (record) {
      setPreferences(record);
      applyCookiePreferences(record);
    }
    if (shouldReloadAfterOptionalConsentRevocation) {
      window.location.reload();
      return;
    }
    setVisible(false);
    setShowSettings(false);
  };

  const acceptAll = () =>
    save({
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
    });
  const rejectAll = () => save(defaultCookiePreferences);
  if (!visible) return null;

  return (
    <div
      ref={panelRef}
      className="cookie-panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-consent-title"
    >
      <div className="cookie-icon">
        <Cookie size={23} />
      </div>
      <div className="cookie-panel-content">
        <h2 id="cookie-consent-title">Twoja prywatność</h2>
        {showSettings ? (
          <>
            <p>
              Wybierz zakres opcjonalnych cookies. Zgodę możesz zmienić w każdej
              chwili przez link w stopce.
            </p>
            <div className="cookie-category-list">
              {cookieCategories.map(category => (
                <section key={category.id} className="cookie-category">
                  <div>
                    <h3>{category.label}</h3>
                    <p>{category.description}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={preferences[category.id]}
                    aria-label={`${category.label}: ${preferences[category.id] ? "włączone" : "wyłączone"}`}
                    disabled={category.locked}
                    className="cookie-switch"
                    onClick={() =>
                      setPreferences(current => ({
                        ...current,
                        [category.id]: !current[category.id],
                      }))
                    }
                  >
                    {preferences[category.id] ? "Włączone" : "Wyłączone"}
                  </button>
                  <details>
                    <summary>Lista plików cookies</summary>
                    {category.declarations.length ? (
                      <ul>
                        {category.declarations.map(item => (
                          <li key={item.name}>
                            <strong>{item.name}</strong>
                            <span>Dostawca: {item.provider}</span>
                            <span>Cel: {item.purpose}</span>
                            <span>Czas: {item.duration}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p>
                        {category.noCookiesMessage ??
                          "W tej kategorii nie zapisujemy plików cookies."}
                      </p>
                    )}
                  </details>
                </section>
              ))}
            </div>
            <div className="cookie-actions cookie-actions-settings">
              <button
                ref={primaryActionRef}
                className="cookie-save"
                type="button"
                onClick={() => save(preferences)}
              >
                Zapisz wybór
              </button>
              <button
                className="cookie-accept"
                type="button"
                onClick={acceptAll}
              >
                Akceptuj wszystkie
              </button>
              <button
                className="cookie-reject"
                type="button"
                onClick={rejectAll}
              >
                Odrzuć wszystkie
              </button>
            </div>
            <button
              className="cookie-export"
              type="button"
              onClick={downloadConsentRegister}
            >
              Pobierz rejestr zgód CSV
            </button>
          </>
        ) : (
          <>
            <p>
              Administratorem danych jest Expert Partner Sp. z o.o. Używamy
              cookies niezbędnych do działania serwisu oraz - wyłącznie za
              dobrowolną zgodą - do funkcji zewnętrznych, analityki i
              marketingu. Zgodę możesz wycofać w każdej chwili. Szczegóły
              znajdziesz w{" "}
              <a href="/polityka-prywatnosci">polityce prywatności</a>.
            </p>
            <div className="cookie-actions">
              <button
                ref={primaryActionRef}
                className="cookie-accept"
                type="button"
                onClick={acceptAll}
              >
                Akceptuj wszystkie
              </button>
              <button
                className="cookie-reject"
                type="button"
                onClick={rejectAll}
              >
                Odrzuć wszystkie
              </button>
              <button
                className="cookie-settings"
                type="button"
                onClick={() => setShowSettings(true)}
              >
                <Settings2 size={16} /> Ustawienia
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
